import type { BuildContext, Fragment } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import { countdownSchema, type CountdownProps } from "../../templates/index.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { tl } from "../timeline.js";
import { assemble } from "./shared.js";

export const COUNTDOWN_DURATION_SEC = 8;

export function buildCountdownCtaHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = countdownSchema.parse(rawProps) as CountdownProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const fullCtx: BuildContext = { ...ctx, durationSec: COUNTDOWN_DURATION_SEC };

  const numberSize = scaleFor(ctx.format, { base: 420, short: 520, square: 380, wide: 460 });
  const hookSize = scaleFor(ctx.format, { base: 80, short: 92, square: 72, wide: 86 });
  const offerSize = scaleFor(ctx.format, { base: 52, short: 60, square: 48, wide: 56 });
  const ctaSize = scaleFor(ctx.format, { base: 56, short: 64, square: 50, wide: 60 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  // Sequences from CountdownCTA:
  //   hook:    frame  0..45   (1.5s)
  //   counter: frame 45..185  (4.67s)
  //   offer:   frame 50..350  (visible to end)
  //   cta:     frame 150..270 (4.0s)
  const hookStart = 0;
  const hookDur = 45 / 30;
  const counterStart = 45 / 30;
  const counterDur = 140 / 30;
  const offerStart = 50 / 30;
  const offerDur = COUNTDOWN_DURATION_SEC - offerStart;
  const ctaStart = 150 / 30;
  const ctaDur = 120 / 30;

  const bg = backgroundGradient(fullCtx, "bg", { variant: "accent", animated: true });
  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });

  const hookFrag = hookText({ id: "tpl-hook", text: props.hook, startSec: hookStart, size: hookSize, color: "#fff", align: "center" });

  // Counter: discrete integer tick from `start` down to 0 over `counterDur`
  // (matches Remotion `Math.ceil(start - elapsed * 2.5)`).
  const ticksPerSec = 2.5;
  const counterTicks: { sec: number; value: number }[] = [];
  const startValue = props.startNumber;
  const endValue = Math.max(0, props.startNumber - Math.ceil(counterDur * ticksPerSec));
  for (let v = startValue; v >= endValue; v -= 1) {
    const tickSec = counterStart + (startValue - v) / ticksPerSec;
    counterTicks.push({ sec: tickSec, value: v });
  }

  const counterTweens: string[] = [];
  // The display element shows a single number we mutate via gsap.set + innerText.
  // gsap.set with a "raw" call-back via `onComplete` isn't string-safe, so we
  // emit raw .call() steps that push innerText.
  counterTicks.forEach(({ sec, value }) => {
    counterTweens.push(`tl.call(() => { const n = document.getElementById("tpl-counter-val"); if (n) n.textContent = ${JSON.stringify(String(value))}; }, undefined, ${Number(sec.toFixed(4))});`);
  });

  // Continuous pulse — 1 → 1.04 → 1 every second, looped over counterDur
  const counterPulses: string[] = [];
  const pulseCount = Math.floor(counterDur);
  for (let i = 0; i < pulseCount; i++) {
    const t = counterStart + i;
    counterPulses.push(tl.to("#tpl-counter-val", { scale: 1.04, duration: 0.5, ease: "sine.inOut" }, t));
    counterPulses.push(tl.to("#tpl-counter-val", { scale: 1, duration: 0.5, ease: "sine.inOut" }, t + 0.5));
  }

  const counterFrag: Fragment = {
    html: `<div id="tpl-counter" style="font-weight:900;font-size:${numberSize}px;color:white;line-height:0.95;letter-spacing:-10px;text-shadow:0 24px 48px rgba(0,0,0,0.35);"><span id="tpl-counter-val">${startValue}</span></div>`,
    init: `gsap.set("#tpl-counter-val", { scale: 1, transformOrigin: "center" });`,
    tweens: [...counterTweens, ...counterPulses],
  };

  const offerFrag: Fragment = {
    html: `<div id="tpl-offer" style="font-weight:700;font-size:${offerSize}px;color:white;text-wrap:balance;">${escapeHtml(props.offer)}</div>`,
    init: `gsap.set("#tpl-offer", { opacity: 0, y: 18 });`,
    tweens: [tl.fromTo("#tpl-offer", { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, offerStart)],
  };

  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const limitedBadge = `<div id="tpl-limited" style="color:${colors.bg};font-weight:800;font-size:${Math.round(offerSize * 0.55)}px;background:white;padding:10px 24px;border-radius:9999px;letter-spacing:1px;text-transform:uppercase;">${escapeHtml(loc.labels.limitedTime)}</div>`;

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div id="tpl-center" style="flex:1;display:flex;flex-direction:column;justify-content:center;align-items:center;gap:28px;text-align:center;">
  <div id="tpl-hook-clip" class="clip" data-start="${hookStart}" data-duration="${hookDur}" data-track-index="0">${hookFrag.html}</div>
  <div id="tpl-counter-clip" class="clip" data-start="${counterStart}" data-duration="${counterDur}" data-track-index="1">${counterFrag.html}</div>
  <div id="tpl-offer-clip" class="clip" data-start="${offerStart}" data-duration="${offerDur}" data-track-index="2">${offerFrag.html}</div>
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="3" style="display:flex;flex-direction:column;align-items:center;gap:14px;">
  ${limitedBadge}
  ${ctaFrag.html}
</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  return assemble({
    templateId: "CountdownCTA",
    ctx: fullCtx,
    background: bg,
    contentFragments: [logoFrag, hookFrag, counterFrag, offerFrag, ctaFrag],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
